import * as React from "react";
import {
    Box,
    Grid,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Typography,
    Button,
} from "@mui/material";
import { LOCAL_CONSTANT } from "../Const";
import {
    tableStyle,
    tableHeaderStyle,
    tableCellStyle,
    tableHeaderSmallStyle,
    tableCellSmallStyle,
    tableHeaderDateStyle,
    tableCellDateStyle,
    tableHeaderNameStyle,
    tableCellNameStyle,
} from "../components/ChangeHistoryTableStyle";
import { apiRequest } from "../../api/ApiCall";
import { useUtilityContext } from "../../utility-provider";
import FilterModal from "../components/FilterModal";
import {
    FilterCriteria,
    initFilterCriteriaObj,
    FilterOptions,
    initFilterOptionsObj,
    ChangeHistoryItem,
    initChangeHistoryList
} from "../../data/type";
import { decryptQueryParam } from '../../utility/util';
import dayjs from "dayjs";
import {
    changeHistoryTableName,
    changeHistoryColumnName,
    changeHistorySelecterDisplay,
    addSortColumnToData,
    generateFilterOptions,
    applyFilter,
    dateSort,
    pageSort,
} from "../components/ChangeHistoryFunc";
import { useUserContext } from "../../user-provider";
import { usePlanAreaSearchByIdForHeader } from "../Common";

export interface EvaluateChangeHistoryProps {
    props: {
        width: number;
        height: number;
    };
}

const EvaluateChangeHistory: React.FC<EvaluateChangeHistoryProps> = ({
    props,
}) => {
    const utilityCtx = useUtilityContext();
    //URLパラメータ
    const [planAreaId, setPlanAreaId] = React.useState("");
    const [changeHistory, setChangeHistory] = React.useState<ChangeHistoryItem[]>(initChangeHistoryList);
    const [tableNameMaster, setTableNameMaster] = React.useState<any>([]);
    const [codeMaster, setCodeMaster] = React.useState<any>();
    
    // フィルタ関連の状態管理
    const [filterModalOpen, setFilterModalOpen] = React.useState<boolean>(false); // フィルタモーダルの開閉状態
    const [originalChangeHistory, setOriginalChangeHistory] = React.useState<ChangeHistoryItem[]>(initChangeHistoryList); // フィルタ前の元データ
    const [filterCriteria, setFilterCriteria] = React.useState<FilterCriteria>(initFilterCriteriaObj);  // 現在のフィルタ条件
    const [filterOptions, setFilterOptions] = React.useState<FilterOptions>(initFilterOptionsObj); // フィルタ選択肢
    
    const user = useUserContext();
    const updateHeader = usePlanAreaSearchByIdForHeader();


    React.useEffect(() => {
        //暗号化されたクエリパラメータを復号化
        const search = window.location.search;
        const urlParam = search.substring(1);
        decryptQueryParam(urlParam).then((res) => {
            setPlanAreaId(res[0]);
        }).catch((e:any) => {
            console.log(e);
        });
        const tableNameMasterObj = localStorage.getItem("tableNameMaster");
        if (tableNameMasterObj) {
            setTableNameMaster(JSON.parse(tableNameMasterObj));
        }
        const res: any = localStorage.getItem("codeMaster");
        if (res) {
            const d = JSON.parse(res);
            setCodeMaster(d);
        }
    }, []);

    React.useEffect(() => {
        if(planAreaId !== "" ) {
            callChangeHistory();
            // 候補地情報をRedux storeに設定（共有関数を使用）
            if (user.userData) {
                updateHeader(planAreaId, user.userData);
            }
        }
    },[planAreaId, user.userData]);

    const callChangeHistory = () => {
        const params = {
            mode: "regist",
            endPoint: "/location/system/v1/get-log",
            query: {
                plan_area_id: planAreaId,
                log_type: "rr",
            },
        };
        if (utilityCtx.showSpinner) {
            utilityCtx.showSpinner();
        }
        (async () => {
            try {
                const res = await apiRequest(params);
                // データにsort_columnを追加する処理 ページソートで使用するため
                const enhancedData = addSortColumnToData(res.data, codeMaster, tableNameMaster);
                setOriginalChangeHistory(enhancedData);
                setChangeHistory(enhancedData);
                const filterOpts = generateFilterOptions(enhancedData, codeMaster);
                setFilterOptions(filterOpts);
                if (utilityCtx.hideSpinner) {
                    utilityCtx.hideSpinner();
                }
            } catch (e) {
                console.log(e);
                if (utilityCtx.hideSpinner) {
                    utilityCtx.hideSpinner();
                }
            }
        })();
    };

    const change_details = (log_type: string) => {
        const change_details_list = [
            LOCAL_CONSTANT.SYSTEM_LOG_TYPE.ITEM_CHANGE, // "0"
            "",
            "",
            LOCAL_CONSTANT.SYSTEM_LOG_TYPE.CONFLICT_CVS_CHANGE, // "3"
            LOCAL_CONSTANT.SYSTEM_LOG_TYPE.CONFLICT_SMDS_CHANGE, // "4"
            LOCAL_CONSTANT.SYSTEM_LOG_TYPE.IMPACT_INTERSECTION_CHANGE, // "5"
            LOCAL_CONSTANT.SYSTEM_LOG_TYPE.SALES_FORECAST_SUMMARY_TABLE_CHANGE, // "6"
            LOCAL_CONSTANT.SYSTEM_LOG_TYPE.ADDITIONAL_SURVEY_CHANGE, // "7"
            LOCAL_CONSTANT.SYSTEM_LOG_TYPE.OTHER_DOCUMENT_CHANGE, // "8"
            LOCAL_CONSTANT.SYSTEM_LOG_TYPE.RR_INTERIOR_CHANGE, // "9"
            LOCAL_CONSTANT.SYSTEM_LOG_TYPE.CURRENT_STATUS_PHOTO_CHANGE, // "10"
            LOCAL_CONSTANT.SYSTEM_LOG_TYPE.TRAFFIC_VOLUME_CHANGE, // "11"
            LOCAL_CONSTANT.SYSTEM_LOG_TYPE.FIVEM_EXTRA_CALCULATION_BASIS_CHANGE, // "12"
            LOCAL_CONSTANT.SYSTEM_LOG_TYPE.TRADE_AREA_CUT_CHANGE, // 13
            LOCAL_CONSTANT.SYSTEM_LOG_TYPE.IMPACT_ASSESSMENT_CHANGE, // 14
            LOCAL_CONSTANT.SYSTEM_LOG_TYPE.MAP_CHANGE, // 15
        ];

        return change_details_list[Number(log_type)];
    };

    const displayTime = (date: string) => {
        const time = new Date(date);
        time.setHours(time.getHours() + 9);
        return dayjs(time).format("YYYY-MM-DD HH:mm:ss");
    };

    // フィルタを適用
    const handleApplyFilter = (filters: FilterCriteria) => {
        const filteredData = applyFilter(originalChangeHistory, filters, tableNameMaster);
        setChangeHistory(filteredData);
        setFilterCriteria(filters);
    };

    // フィルタモーダルを開く
    const handleFilterOpen = () => {
        setFilterModalOpen(true);
    };

    // フィルタモーダルを閉じる
    const handleFilterClose = () => {
        setFilterModalOpen(false);
    };

    // ページソート
    const handlePageSort = () => {
        const sortedData = pageSort(changeHistory);
        setChangeHistory(sortedData);
    };

    // 日付ソート
    const handleDateSort = () => {
        const sortedData = dateSort(changeHistory);
        setChangeHistory(sortedData);
    };

    return (
        <Box sx={{ width: "100%" }}>
            <Grid container>
                <Grid item xs={12} sx={{ height: "48px" }} />
            </Grid>
            <Grid container direction={"row"} sx={{ position: "fixed" }}>
                <Grid item sx={{ width: "55px", height: props.height }} />
                <Grid item sx={{ width: props.width, height: props.height, overflowY: "scroll" }}>
                    <Grid item sx={{ marginTop: "10px", marginLeft: "10px", marginBottom: "10px" }}>
                        <Box sx={{ 
                            display: "flex", 
                            alignItems: "center",
                            minHeight: "48px",
                            paddingY: "8px"
                        }}>
                            <Typography sx={{ display: "flex", alignItems: "center" }}>
                                {LOCAL_CONSTANT.LABEL.RR_EVALUATE_CHANGE_HISTORY}
                            </Typography>
                            <Button
                                variant="contained"
                                color="success"
                                size="small"
                                onClick={handleFilterOpen}
                                sx={{ 
                                    margin: "0px 10px",
                                    display: "flex",
                                    alignItems: "center",
                                }}
                            >
                                {LOCAL_CONSTANT.LABEL.FILTER}
                            </Button>
                            <Button
                                variant="contained"
                                color="success"
                                size="small"
                                onClick={handlePageSort}
                                sx={{ 
                                    margin: "0px 10px",
                                    display: "flex",
                                    alignItems: "center",
                                }}
                            >
                                {LOCAL_CONSTANT.LABEL.PAGE_SORT}
                            </Button>
                            <Button
                                variant="contained"
                                color="success"
                                size="small"
                                onClick={handleDateSort}
                                sx={{ 
                                    margin: "0px 10px",
                                    display: "flex",
                                    alignItems: "center",
                                }}
                            >
                                {LOCAL_CONSTANT.LABEL.DATE_SORT}
                            </Button>
                        </Box>
                    </Grid>
                    <TableContainer sx={{ height: "90%", minHeight: "90%", maxHeight: "90%", overflowY: "auto"}}>
                        <Table stickyHeader sx={tableStyle}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={tableHeaderDateStyle}>{LOCAL_CONSTANT.LABEL.DATE}</TableCell>
                                    <TableCell sx={tableHeaderNameStyle}>{LOCAL_CONSTANT.LABEL.EDITOR}</TableCell>
                                    <TableCell sx={tableHeaderSmallStyle}>{LOCAL_CONSTANT.LABEL.BRANCH}</TableCell>
                                    <TableCell sx={tableHeaderStyle}>{LOCAL_CONSTANT.LABEL.PAGE}</TableCell>
                                    <TableCell sx={tableHeaderStyle}>{LOCAL_CONSTANT.LABEL.ITEM}</TableCell>
                                    <TableCell sx={tableHeaderStyle}>{LOCAL_CONSTANT.LABEL.CHANGE_BEFORE}</TableCell>
                                    <TableCell sx={tableHeaderStyle}>{LOCAL_CONSTANT.LABEL.AFTER_CHANGE}</TableCell>
                                    <TableCell sx={tableHeaderStyle}>{LOCAL_CONSTANT.LABEL.CHANGE_DETAILS}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {changeHistory.map(
                                    (row: any, index: number) => (
                                        <TableRow key={ row.primaryKey + "-time-" + row.publish_time + "-" + index }>
                                            <TableCell sx={tableCellDateStyle}>
                                                {displayTime(row.publish_time)}
                                            </TableCell>
                                            <TableCell sx={tableCellNameStyle}>{row.update_name}</TableCell>
                                            <TableCell sx={tableCellSmallStyle}>{row.branch}</TableCell>
                                            <TableCell sx={tableCellStyle}>{changeHistoryTableName(tableNameMaster, row.target_table, row)}</TableCell>
                                            <TableCell sx={tableCellStyle}>{changeHistoryColumnName(tableNameMaster, row.target_column, row)}</TableCell>
                                            <TableCell sx={tableCellStyle}>{changeHistorySelecterDisplay(codeMaster, row.target_column, row.target_before_value)}</TableCell>
                                            <TableCell sx={tableCellStyle}>{changeHistorySelecterDisplay(codeMaster, row.target_column, row.target_after_value )}</TableCell>
                                            <TableCell sx={tableCellStyle}>{change_details(row.log_type)}</TableCell>    
                                        </TableRow>
                                    )
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            </Grid>
            <FilterModal
                open={filterModalOpen}
                onClose={handleFilterClose}
                onApplyFilter={handleApplyFilter}
                filterOptions={filterOptions}
                currentFilters={filterCriteria}
            />
        </Box>
    );
};

export default EvaluateChangeHistory;
